import { type ComponentPropsWithoutRef, CSSProperties, ReactNode, useState } from "react";
import { CgSpinner } from "react-icons/cg";

import { convertHexToRgba, filterClasses } from "@swivl-mono/helpers";
import "./Button.scss"

export enum ButtonVariant {
  PRIMARY = "primary",
  GHOST = "ghost",
  DASHED = "dashed",
  ICON = "icon",
  FLOAT = "float",
}

export enum ButtonSize {
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
}

export enum ButtonColor {
  ORANGE = "orange",
  YELLOW = "yellow",
  RED = "red",
  TEAL = "teal",
  BLUE = "blue",
  BLACK = "black",
  WHITE = "white",
  DARK_GREY = "darkGrey",
  LIGHT_GREY = "lightGrey",
  TRANSPARENT = "transparent",
  THEMED = "themed"
}

type Outlets = "container" | "content" | "leftIcon" | "spinner";

interface ButtonProps extends Omit<ComponentPropsWithoutRef<"button">, "className" | "disabled"> {
  children: ReactNode;
  className?: string;
  classNames?: { [Key in Outlets]: string };
  disabled?: boolean;
  leftIcon?: ReactNode;
  loading?: boolean;
  onClickAsync?: () => Promise<void>;
  color?: ButtonColor;
  size?: ButtonSize;
  variant?: ButtonVariant;
  fullWidth?: boolean;
}

// colors for button background / gradient and text
const buttonColorMap: Record<ButtonColor, { main: string; accent?: string; text: string }> = {
  [ButtonColor.ORANGE]: { accent: "#ff7f3a", main: "#ff5b02", text: "#ffffff" },
  [ButtonColor.TEAL]: { accent: "#01cbc0", main: "#00aaa2", text: "#ffffff" },
  [ButtonColor.WHITE]: { accent: "#ffffff", main: "#f5f5f5", text: "#000000" },
  [ButtonColor.YELLOW]: { accent: "#ffd41e", main: "#ffb801", text: "#000000" },
  [ButtonColor.BLACK]: { accent: "#323232", main: "#1b1b1b", text: "#ffffff" },
  [ButtonColor.TRANSPARENT]: { main: "transparent", text: "#000000" },
  [ButtonColor.LIGHT_GREY]: { main: "#d9d9d9", accent: "#bfbfbf", text: "#000000" },
  [ButtonColor.BLUE]: { main: "#007bff", accent: "#0056b3", text: "#ffffff" },
  [ButtonColor.DARK_GREY]: { main: "#707070", accent: "#4b4b4b", text: "#ffffff" },
  [ButtonColor.RED]: { main: "#ff4b34", accent: "#e71b00", text: "#ffffff" },
  [ButtonColor.THEMED]: {
    main: "var(--swivl-primaryBackground)",
    accent: "color-mix(in_srgb,var(--swivl-primaryBackground)70%,black_30%)",
    text: "var(--swivl-primaryText)",
  }
}

const getButtonColor = (color: ButtonColor, disabled?: boolean) => {
  if (disabled) {
    return {
      "--btn-main": "#dddddd",
      "--btn-accent": "#dddddd",
      "--btn-text": "#a8a8a8",
      "--btn-bg": `linear-gradient(180deg, #dddddd, #dddddd)`
    }
  }

  const { main, accent, text } = buttonColorMap[color];
  return {
    "--btn-main": main,
    "--btn-accent": accent ?? main,
    "--btn-text": text,
    "--btn-bg": `linear-gradient(180deg, ${accent ?? main}, ${main}, ${accent ?? main})`,
    "--btn-text-tap": convertHexToRgba(text, 0.7),
  }
}

const BaseButton = ({
  children,
  className,
  classNames,
  color = ButtonColor.BLACK,
  disabled = false,
  leftIcon = null,
  loading = false,
  onClick,
  onClickAsync,
  size = ButtonSize.MEDIUM,
  variant = ButtonVariant.PRIMARY,
  fullWidth = false,
  ...rest
}: ButtonProps) => {
  const [isLoading, setIsLoading] = useState(loading ?? false);

  // internal class names for sizes and variants
  const sharedSizeClasses: Record<ButtonSize, string> = {
    [ButtonSize.SMALL]: "px-3 h-[26px] min-w-[64px] text-sm",
    [ButtonSize.MEDIUM]: "px-3 h-8 min-w-[64px] text-sm",
    [ButtonSize.LARGE]: "px-3 h-10 min-w-[64px] text-base",
  }

  const buttonVariantClassNames: Record<ButtonVariant, {
    baseClasses: string;
    sizes?: Record<ButtonSize, string>;
  }> = {
    [ButtonVariant.PRIMARY]: {
      baseClasses: filterClasses(
        // button-primary (button.scss) is handling shadows -- shadows are not animating in tw well -- handling with css vars in tw 4 should work ;)
        "button-primary rounded-[3px] text-[var(--btn-text)] border-none",
        "bg-[image:var(--btn-bg)] bg-[length:200%_200%] bg-[position:0%_0%]",
        "hover:bg-[position:0%_100%] transition-[background-position] duration-500 ease-in-out",
        "active:text-[var(--btn-text-tap)] active:fill-[var(--btn-text-tap)] duration-100",
        "transition-all ease-in-out",
      ),
      sizes: sharedSizeClasses
    },
    [ButtonVariant.GHOST]: {
      baseClasses: filterClasses(
        `bg-transparent ${color === ButtonColor.WHITE ? "text-white" : "text-black"}`,
        "hover:text-orange-500 fill-orange-500 duration-200",
        "active:text-amber-450 active:fill-amber-450 duration-100",
        "transition-colors ease-in-out",
      ),
      sizes: sharedSizeClasses
    },
    [ButtonVariant.DASHED]: {
      baseClasses: filterClasses(
        "bg-white border border-dashed text-black rounded-[4px]",
        "aria-expanded:border-orange-500 aria-expanded:bg-orange-200",
        "hover:text-orange-500 hover:border-orange-500 hover:bg-[#ffefe6] duration-200",
        "active:text-amber-450 active:fill-amber-450 active:border-amber-450 active:bg-[#fffbe6] duration-100",
        "transition-colors ease-in-out",
      ),
      sizes: sharedSizeClasses
    },
    [ButtonVariant.ICON]: {
      baseClasses: filterClasses(
        "rounded-[4px] button-primary text-[var(--btn-text)] border-none",
        "bg-[image:var(--btn-bg)] bg-[length:200%_200%] bg-[position:0%_0%] ",
        "hover:bg-[position:0%_100%] ",
        "transition-[background-position] duration-500 ease-in-out",
        "active:text-[var(--btn-text-tap)] active:fill-[var(--btn-text-tap)] duration-100",
        "transition-all ease-in-out"),
      sizes: {
        [ButtonSize.SMALL]: "h-[26px] w-[30px]",
        [ButtonSize.MEDIUM]: "h-8 w-9",
        [ButtonSize.LARGE]: "h-10 w-11",
      },
    },
    [ButtonVariant.FLOAT]: {
      baseClasses: filterClasses(
        "button-float rounded-full shadow-lg size-[26px] bg-[linear-gradient(180deg,#ffffff,#f5f5f5)] fill-orange-500",
        "hover:bg-[linear-gradient(180deg,#ff5b02,#ff7f3a)] hover:fill-white hover:scale-110",
        "transition-all duration-300 ease-in-out",
      ),
    },
  };

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (loading || disabled) {
      return;
    }

    if (onClickAsync) {
      try {
        setIsLoading(true);
        await onClickAsync();
      } catch (error) {
        console.error("Error occurred:", error);
      } finally {
        setIsLoading(false);
      }
    } else if (onClick) {
      onClick(e);
    }
  }

  const resolvedClassName = className || classNames?.container || "";

  return (
    <button
      style={getButtonColor(color, disabled) as CSSProperties}
      className={
        filterClasses(
          "relative font-semibold gap-1 fill-current flex items-center justify-center",
          variant === ButtonVariant.GHOST ? "disabled:bg-grey-400" : "disabled:text-grey-500 disabled:fill-grey-500",
          "disabled:cursor-not-allowed",
          isLoading ? "cursor-not-allowed pointer-events-none opacity-80" : "pointer-events-auto",
          fullWidth ? "w-full" : variant !== ButtonVariant.ICON && variant !== ButtonVariant.FLOAT ? "w-fit" : "",
          // variant classes
          buttonVariantClassNames[variant]?.baseClasses,
          // variant size classes
          (buttonVariantClassNames[variant]?.sizes?.[size] ?? buttonVariantClassNames[variant]?.sizes?.[ButtonSize.MEDIUM]) || "",
          resolvedClassName
        )}
      disabled={disabled}
      onClick={handleClick}
      {...rest}
    >
      {isLoading && (
        <span className={filterClasses(classNames?.spinner)}>
          <CgSpinner className="animate-spin fill-current" />
          <span className="sr-only">loading...</span>
        </span>
      )}
      {leftIcon && (
        <span
          className={filterClasses(
            "text-sm stroke-current",
            classNames?.leftIcon,
            isLoading ? "invisible" : "visible"
          )}
        >
          {leftIcon}
        </span>
      )}
      <div className={
        filterClasses(
          "flex items-center gap-2",
          classNames?.content,
          isLoading && variant === ButtonVariant.ICON ? "invisible" : "visible"
        )}
      >
        {children}
      </div>
    </button>
  );
};

export const Button = BaseButton;
